import { MoreOutlined } from '@ant-design/icons'
import { Popover } from 'antd'
import classNames from 'classnames'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type { CategoryInterface } from '@/types'
import { getStatusBadge } from '@/utils/catalog/approvalStatus'

import cn from '../CatalogApproval.module.scss'
import { type CatalogApprovalColumn } from '../CatalogApprovalTable/CatalogApprovalTable'
import StatusChip from '../StatusChip/StatusChip'

type UseCategoryModerationTableOptions = {
  moderationIds?: Set<string>
  approvedOnly: boolean
  pendingActionKey: string
  rootModerationCounts?: Map<string, number>
  searchQuery: string
  showModerationActions: boolean
  tree: CategoryInterface[]
  onApprove: (record: CategoryInterface['category']) => void
  onOpen: (record: CategoryInterface['category']) => void
  onReject: (record: CategoryInterface['category']) => void
}

type CategoryRow = {
  ancestorIds: string[]
  category: CategoryInterface['category']
  depth: number
  hasChildren: boolean
  id: string
}

const sortTree = (nodes: CategoryInterface[]): CategoryInterface[] =>
  [...nodes]
    .sort((a, b) =>
      a.category.description.localeCompare(b.category.description, 'ru')
    )
    .map((node) => ({
      ...node,
      childCategories: sortTree(node.childCategories || []),
    }))

const filterTree = (
  nodes: CategoryInterface[],
  isKept: (category: CategoryInterface['category']) => boolean
): CategoryInterface[] =>
  nodes.reduce<CategoryInterface[]>((accumulator, node) => {
    const childCategories = filterTree(node.childCategories || [], isKept)
    const shouldKeep = isKept(node.category) || childCategories.length > 0

    if (shouldKeep) {
      accumulator.push({
        ...node,
        childCategories,
      })
    }

    return accumulator
  }, [])

const isApprovedCategory = (category: CategoryInterface['category']) =>
  (category.status ?? 'APPROVED') === 'APPROVED'

const filterTreeByStatus = (
  nodes: CategoryInterface[],
  approvedOnly: boolean,
  moderationIds?: Set<string>
) => {
  if (moderationIds)
    return filterTree(nodes, (category) => moderationIds.has(category.id))
  return approvedOnly ? filterTree(nodes, isApprovedCategory) : nodes
}

const filterTreeBySearch = (
  nodes: CategoryInterface[],
  searchQuery: string
): CategoryInterface[] => {
  const normalizedSearch = searchQuery.trim().toLowerCase()
  if (!normalizedSearch) return nodes

  return nodes.reduce<CategoryInterface[]>((accumulator, node) => {
    const childCategories = filterTreeBySearch(
      node.childCategories || [],
      normalizedSearch
    )
    const isMatched = [
      String(node.category.code),
      node.category.description,
      node.category.status ?? 'APPROVED',
    ].some((value) => value.toLowerCase().includes(normalizedSearch))

    if (isMatched || childCategories.length > 0) {
      accumulator.push({
        ...node,
        childCategories,
      })
    }

    return accumulator
  }, [])
}

const buildVisibleRows = (
  nodes: CategoryInterface[],
  expandedIds: Set<string>,
  searchQuery: string,
  ancestorIds: string[] = [],
  depth = 0
): CategoryRow[] =>
  sortTree(nodes).flatMap((node) => {
    const hasChildren = (node.childCategories || []).length > 0
    const row: CategoryRow = {
      ancestorIds,
      category: node.category,
      depth,
      hasChildren,
      id: node.category.id,
    }

    const shouldShowChildren =
      searchQuery.trim() !== '' || expandedIds.has(node.category.id)

    return shouldShowChildren
      ? [
          row,
          ...buildVisibleRows(
            node.childCategories || [],
            expandedIds,
            searchQuery,
            [...ancestorIds, node.category.id],
            depth + 1
          ),
        ]
      : [row]
  })

const collectExpandableIds = (
  nodes: CategoryInterface[],
  accumulator: Set<string> = new Set()
): Set<string> => {
  nodes.forEach((node) => {
    if ((node.childCategories || []).length > 0) {
      accumulator.add(node.category.id)
      collectExpandableIds(node.childCategories, accumulator)
    }
  })

  return accumulator
}

function useCategoryModerationTable(props: UseCategoryModerationTableOptions) {
  const {
    moderationIds,
    approvedOnly,
    pendingActionKey,
    rootModerationCounts,
    searchQuery,
    showModerationActions,
    tree,
    onApprove,
    onOpen,
    onReject,
  } = props
  const isOrganisationSelected = useUserInfoStore(
    (state) => Object.keys(state.selectedOrganisation).length > 0
  )
  const actionPopoverClassName = classNames(
    cn.actionPopover,
    isOrganisationSelected && cn.organisationSelected
  )

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  const moderationTree = useMemo(
    () => filterTreeByStatus(tree, approvedOnly, moderationIds),
    [approvedOnly, moderationIds, tree]
  )
  const filteredTree = useMemo(
    () => filterTreeBySearch(moderationTree, searchQuery),
    [moderationTree, searchQuery]
  )
  const autoExpandedIds = useMemo(
    () => collectExpandableIds(filteredTree),
    [filteredTree]
  )

  useEffect(() => {
    if (!showModerationActions || searchQuery.trim() !== '') return

    setExpandedIds(autoExpandedIds)
  }, [autoExpandedIds, searchQuery, showModerationActions])

  const rows = useMemo(
    () => buildVisibleRows(filteredTree, expandedIds, searchQuery),
    [expandedIds, filteredTree, searchQuery]
  )

  const toggleExpanded = useCallback((categoryId: string) => {
    setExpandedIds((prevState) => {
      const nextState = new Set(prevState)
      if (nextState.has(categoryId)) nextState.delete(categoryId)
      else nextState.add(categoryId)
      return nextState
    })
  }, [])

  const getRootModerationCount = useCallback(
    (row: CategoryRow) =>
      row.depth === 0 ? (rootModerationCounts?.get(row.id) ?? 0) : 0,
    [rootModerationCounts]
  )

  const getRowStateClassName = useCallback(
    (row: CategoryRow) => {
      const isSearchEmpty = searchQuery.trim() === ''
      const moderationCount = getRootModerationCount(row)

      // Строка является открытой (нажат крестик/стрелочка)
      const isExpandedRow = isSearchEmpty && expandedIds.has(row.id)

      // Строка является потомком открытой строки, но САМА НЕ ОТКРЫТА
      const isExpandedDescendant =
        isSearchEmpty &&
        !isExpandedRow && // <-- Добавлено это условие
        row.ancestorIds.some((ancestorId) => expandedIds.has(ancestorId))

      return [
        isExpandedRow ? cn.categoryTreeRowExpanded : '',
        isExpandedDescendant ? cn.categoryTreeRowDescendant : '',
        moderationCount > 0 ? cn.categoryTreeRowModerationRoot : '',
      ]
        .filter(Boolean)
        .join(' ')
    },
    [expandedIds, getRootModerationCount, searchQuery]
  )

  const renderModerationActions = useCallback(
    (row: CategoryRow) => (
      <Popover
        trigger="hover"
        placement="bottomRight"
        overlayClassName={actionPopoverClassName}
        content={
          <div
            className={cn.actionMenu}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              className={cn.actionMenuButton}
              onClick={() => onOpen(row.category)}
            >
              Открыть карточку
            </button>
            <button
              className={cn.actionMenuButton}
              disabled={
                pendingActionKey === `category-${row.category.id}-APPROVED`
              }
              onClick={() => onApprove(row.category)}
            >
              Утвердить
            </button>
            <button
              className={cn.actionMenuDangerButton}
              disabled={
                pendingActionKey === `category-${row.category.id}-REJECTED`
              }
              onClick={() => onReject(row.category)}
            >
              Отклонить
            </button>
          </div>
        }
      >
        <button
          type="button"
          className={cn.actionTriggerButton}
          aria-label="Действия с категорией"
          onClick={(event) => event.stopPropagation()}
        >
          <MoreOutlined />
        </button>
      </Popover>
    ),
    [actionPopoverClassName, onApprove, onOpen, onReject, pendingActionKey]
  )

  const columns = useMemo<CatalogApprovalColumn<CategoryRow>[]>(
    () => [
      {
        id: 'code',
        header: 'Код',
        width: '120px',
        cell: ({ row }) => row.original.category.code,
      },
      {
        id: 'name',
        header: () => (
          <span className={cn.categoryColumnHeader}>Категория</span>
        ),
        width: 'minmax(320px, 2fr)',
        cell: ({ row }) => {
          const record = row.original
          const moderationCount = getRootModerationCount(record)

          return (
            <div className={cn.categoryColumnCell}>
              <div
                className={cn.categoryTreeNameWrap}
                style={{ '--tree-depth': record.depth }}
              >
                {record.hasChildren ? (
                  <button
                    type="button"
                    className={cn.categoryTreeExpandButton}
                    onClick={(event) => {
                      event.stopPropagation()
                      toggleExpanded(record.id)
                    }}
                  >
                    {searchQuery.trim() !== '' || expandedIds.has(record.id)
                      ? '▾'
                      : '▸'}
                  </button>
                ) : (
                  <span className={cn.categoryTreeExpandPlaceholder} />
                )}
                <span className={cn.categoryTreeNameContent}>
                  <span className={cn.categoryTreeName}>
                    {record.category.description}
                  </span>
                  {moderationCount > 0 && (
                    <span
                      className={cn.categoryModerationBadge}
                      title={`На модерации в этой ветке: ${moderationCount}`}
                    >
                      {moderationCount}
                    </span>
                  )}
                </span>
              </div>
            </div>
          )
        },
      },
      {
        id: 'status',
        header: 'Статус',
        width: '190px',
        cell: ({ row }) => <StatusChip status={row.original.category.status} />,
      },
      {
        id: 'actions',
        header: 'Действие',
        width: showModerationActions ? '96px' : '200px',
        cell: ({ row }) =>
          showModerationActions ? (
            renderModerationActions(row.original)
          ) : (
            <button
              className={cn.rowActionButton}
              onClick={(event) => {
                event.stopPropagation()
                onOpen(row.original.category)
              }}
            >
              Открыть карточку
            </button>
          ),
      },
    ],
    [
      expandedIds,
      getRootModerationCount,
      onOpen,
      renderModerationActions,
      searchQuery,
      showModerationActions,
      toggleExpanded,
    ]
  )

  const renderMobileCard = useCallback(
    (row: CategoryRow) => (
      <div className={cn.mobileCard}>
        <div className={cn.mobileCardHeader}>
          <div>
            <div className={cn.mobileCardCode}>Код {row.category.code}</div>
            <div className={cn.mobileCardTitle}>{row.category.description}</div>
          </div>
          {getStatusBadge(row.category.status) ?? (
            <span className={cn.mobileCardLabel}>Утверждена</span>
          )}
        </div>

        <div className={cn.mobileCardMeta}>
          <div className={cn.mobileCardRow}>
            <span className={cn.mobileCardLabel}>Статус</span>
            <div className={cn.mobileCardValue}>
              {getStatusBadge(row.category.status) ?? 'Утверждена'}
            </div>
          </div>
        </div>

        <div className={cn.mobileCardActions}>
          <button
            className={cn.rowActionButton}
            onClick={(event) => {
              event.stopPropagation()
              onOpen(row.category)
            }}
          >
            Открыть карточку
          </button>
          {showModerationActions && (
            <>
              <button
                className={cn.rowActionButton}
                disabled={
                  pendingActionKey === `category-${row.category.id}-APPROVED`
                }
                onClick={(event) => {
                  event.stopPropagation()
                  onApprove(row.category)
                }}
              >
                Утвердить
              </button>
              <button
                className={cn.rowDangerActionButton}
                disabled={
                  pendingActionKey === `category-${row.category.id}-REJECTED`
                }
                onClick={(event) => {
                  event.stopPropagation()
                  onReject(row.category)
                }}
              >
                Отклонить
              </button>
            </>
          )}
        </div>
      </div>
    ),
    [onApprove, onOpen, onReject, pendingActionKey, showModerationActions]
  )

  return {
    rowKey: (row: CategoryRow) => row.id,
    columns,
    data: rows,
    getRowClassName: getRowStateClassName,
    estimateRowHeight: 68,
    renderMobileCard,
  }
}

export default useCategoryModerationTable
